import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Map, MapPin, Plus, Loader2, Trash2, UserPlus, Users, FileText, LayoutDashboard, RefreshCw } from "lucide-react";

export default function ZoneManagement() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [districts, setDistricts] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/zones");
      setZones(res.data.zones);
      setCanManage(res.data.can_manage);
    } catch {
      toast.error("Failed to load zones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createZone = async () => {
    if (!name.trim()) { toast.error("Enter a zone name"); return; }
    setBusy(true);
    try {
      await api.post("/admin/zones", { name, code, districts: districts.split(",").map((d) => d.trim()).filter(Boolean) });
      toast.success(`Zone "${name}" created`);
      setName(""); setCode(""); setDistricts("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create zone");
    } finally {
      setBusy(false);
    }
  };

  const removeZone = async (z) => {
    setBusy(true);
    try {
      await api.delete(`/admin/zones/${z.id}`);
      toast.success(`Zone "${z.name}" removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove zone");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950 flex items-center gap-2"><Map className="h-7 w-7 text-emerald-700" /> Zone Management</h1>
            <p className="mt-1.5 text-slate-500">Create operational zones and assign staff. Applications are auto-tagged to a zone by applicant district.</p>
          </div>
          <Button variant="ghost" className="rounded-full text-emerald-700 h-10" onClick={() => navigate("/admin")} data-testid="zone-back-console"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Console</Button>
        </div>

        {canManage && (
          <Card className="mt-6 rounded-3xl border-emerald-100 p-6" data-testid="zone-create-card">
            <h3 className="font-head font-bold text-emerald-950 mb-3">Create a new zone</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-600 text-xs">Zone Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. South Karnataka" className="mt-1 h-11 rounded-xl" data-testid="zone-name-input" />
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. SK" className="mt-1 h-11 rounded-xl" data-testid="zone-code-input" />
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Districts (comma-separated)</Label>
                <Input value={districts} onChange={(e) => setDistricts(e.target.value)} placeholder="Mysuru, Mandya, Hassan" className="mt-1 h-11 rounded-xl" data-testid="zone-districts-input" />
              </div>
            </div>
            <Button onClick={createZone} disabled={busy} className="mt-4 rounded-full bg-brand hover:bg-emerald-800 text-white" data-testid="zone-create-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Create Zone</>}
            </Button>
          </Card>
        )}

        <div className="mt-8">
          <h3 className="font-head font-bold text-emerald-950 mb-3">Zones ({zones.length})</h3>
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : zones.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-emerald-200 p-12 text-center text-slate-400" data-testid="zone-empty">No zones yet. {canManage ? "Create one above." : ""}</Card>
          ) : (
            <div className="space-y-4" data-testid="zone-list">
              {zones.map((z) => <ZoneCard key={z.id} zone={z} canManage={canManage} onChanged={load} onRemove={removeZone} busy={busy} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ZoneCard = ({ zone, canManage, onChanged, onRemove, busy }) => {
  const [email, setEmail] = useState("");
  const [working, setWorking] = useState(false);

  const assign = async () => {
    if (!email.trim()) { toast.error("Enter a staff email"); return; }
    setWorking(true);
    try {
      const res = await api.post(`/admin/zones/${zone.id}/assign`, { email });
      toast.success(`${res.data.email} assigned to ${zone.name}`);
      setEmail("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally {
      setWorking(false);
    }
  };

  const backfill = async () => {
    setWorking(true);
    try {
      const res = await api.post(`/admin/zones/${zone.id}/backfill`);
      toast.success(`${res.data.updated} application(s) tagged to ${zone.name}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card className="rounded-3xl border-emerald-100 p-6" data-testid={`zone-card-${zone.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-head font-bold text-lg text-emerald-950">{zone.name}</span>
            {zone.code && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{zone.code}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {zone.districts?.length ? zone.districts.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1"><MapPin className="h-3 w-3 text-slate-400" /> {d}</span>
            )) : <span className="text-xs text-slate-400">No districts mapped</span>}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5 text-slate-500"><Users className="h-4 w-4 text-emerald-600" /> {zone.staff_count} staff</span>
          <span className="inline-flex items-center gap-1.5 text-slate-500"><FileText className="h-4 w-4 text-emerald-600" /> {zone.application_count} apps</span>
        </div>
      </div>

      {canManage && (
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-emerald-50 pt-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-slate-600 text-xs">Assign staff by email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@email.com" className="mt-1 h-10 rounded-xl" data-testid={`zone-assign-input-${zone.id}`} />
          </div>
          <Button onClick={assign} disabled={working || busy} variant="outline" className="rounded-full border-emerald-200 text-emerald-700 h-10" data-testid={`zone-assign-button-${zone.id}`}>
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Assign</>}
          </Button>
          <Button onClick={backfill} disabled={working || busy} variant="outline" className="rounded-full border-emerald-200 text-emerald-700 h-10" data-testid={`zone-backfill-${zone.id}`}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Tag existing apps
          </Button>
          <Button onClick={() => onRemove(zone)} disabled={working || busy} variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50 h-10" data-testid={`zone-delete-${zone.id}`}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
        </div>
      )}
    </Card>
  );
};
